import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Search, CheckCircle2, Clock } from "lucide-react";
import { adminSignedArtists, adminSetArtistDeal } from "@/lib/afmApi";
import { Loading } from "@/components/common";

const errText = (e) => e?.response?.data?.detail || "No se pudo completar";

// Artistas firmados: todos los artistas de las subcuentas con el acuerdo de Always y el de la compañía.
// Es la base que usarán después los agentes de IA para trabajar cada artista.
export default function AdminFirmados() {
  const [rows, setRows] = useState(undefined);
  const [company, setCompany] = useState("");
  const [q, setQ] = useState("");
  const load = useCallback(() => adminSignedArtists().then(setRows).catch((e) => setRows({ error: errText(e) })), []);
  useEffect(() => { load(); }, [load]);

  const companies = useMemo(() => [...new Map((Array.isArray(rows) ? rows : []).map((r) => [r.manager_id, r.manager_name])).entries()], [rows]);
  const shown = useMemo(() => (Array.isArray(rows) ? rows : []).filter((r) => (!company || r.manager_id === company)
    && (!q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase()))), [rows, company, q]);

  const edit = async (r) => {
    const v = window.prompt(`% de Always para ${r.name} en ${r.manager_name} (por defecto ${r.label_pct_default}%).\nEj.: 50 = 50% Always / 50% compañía. Deja vacío para usar el de la subcuenta.`, r.label_pct_custom ? r.label_pct : "");
    if (v === null) return;
    try { await adminSetArtistDeal(r.manager_id, r.id, v.trim() === "" ? null : +v); toast.success("Acuerdo actualizado"); load(); } catch (e) { toast.error(errText(e)); }
  };

  if (rows === undefined) return <Loading />;
  if (rows.error) return <p className="text-sm text-slate-500">{rows.error}</p>;

  return (
    <div className="space-y-6" data-testid="admin-signed">
      <div>
        <h1 className="flex items-center gap-2 font-serif text-3xl font-extrabold tracking-tight text-black"><BadgeCheck className="h-7 w-7" /> Artistas firmados</h1>
        <p className="mt-1 text-sm text-slate-500">Todos los artistas de tus subcuentas y su acuerdo. El % de Always sale de lo que paga la distribuidora; el resto es de la compañía, que lo reparte con su artista según lo que él firmó.</p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <select value={company} onChange={(e) => setCompany(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">Todas las compañías</option>
          {companies.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar artista" className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm" />
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3 font-semibold">Artista</th>
              <th className="px-3 py-3 font-semibold">Compañía</th>
              <th className="px-3 py-3 font-semibold">Always / compañía</th>
              <th className="px-3 py-3 font-semibold">Artista (de la compañía)</th>
              <th className="px-3 py-3 text-right font-semibold">Lanzamientos</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={`${r.manager_id}-${r.id}`} className="border-t border-slate-100">
                <td className="px-5 py-3 font-medium text-black">{r.name}</td>
                <td className="px-3 py-3 text-slate-600">{r.manager_name}{!r.manager_active && <span className="text-slate-400"> · desactivada</span>}{r.finance_enabled && <span className="ml-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">Finanzas</span>}</td>
                <td className="px-3 py-3"><span className="font-mono font-semibold">{r.label_pct}% / {100 - r.label_pct}%</span>{r.label_pct_custom && <span className="ml-1.5 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">especial</span>}</td>
                <td className="px-3 py-3 text-slate-600">
                  {r.artist_pct != null ? `${r.artist_pct}%` : "Sin reparto"}{" "}
                  {r.split_status === "signed" ? <CheckCircle2 className="inline h-3.5 w-3.5 text-emerald-600" /> : r.artist_pct != null && <Clock className="inline h-3.5 w-3.5 text-amber-600" />}
                </td>
                <td className="px-3 py-3 text-right font-mono tabular-nums">{r.releases}</td>
                <td className="px-5 py-3 text-right"><button onClick={() => edit(r)} className="text-xs font-semibold text-slate-600 hover:text-black">Cambiar % Always</button></td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-400">{rows.length ? "Nada con ese filtro." : "Todavía no hay artistas en subcuentas."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
